import React, {useEffect,useMemo,useRef,useState}  from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import './settings.css'

/* ------------------------------------------------------------------
   Icons — stroke: currentColor so they inherit the token colour.
   ------------------------------------------------------------------ */
const IconDownload=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3v12"/>
    <path d="m7 11 5 5 5-5"/>
    <path d="M4 20h16"/>
  </svg>
)

const IconTrash=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 7h16"/>
    <path d="M9 7V4h6v3"/>
    <path d="M6 7v13h12V7"/>
    <path d="M10 11v6M14 11v6"/>
  </svg>
)

const IconX=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12"/>
  </svg>
)

const IconSearch=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7"/>
    <path d="m20 20-3.5-3.5"/>
  </svg>
)

const IconPaperclip=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M21 11.5 12.5 20a5 5 0 0 1-7-7l8-8a3.5 3.5 0 0 1 5 5l-8 8a2 2 0 0 1-3-3l7.5-7.5"/>
  </svg>
)

/* ------------------------------------------------------------------
   THEME TOKENS
   react-data-table-component styles through a JS object, so the table
   cannot read CSS variables on its own. We read them off :root and
   re-read whenever the theme attribute or the OS preference changes.
   ------------------------------------------------------------------ */
const TOKEN_NAMES=[
  '--panel','--panel-raise','--line','--line-soft',
  '--ink','--ink-2','--ink-3','--accent','--accent-wash',
  '--mono','--sans','--step-0'
]

const readTokens=()=>{
  const out={}
  if (typeof window==='undefined'||!document.documentElement) return out
  const styles=window.getComputedStyle(document.documentElement)
  TOKEN_NAMES.forEach(name=>{
    out[name]=String(styles.getPropertyValue(name)||'').trim()
  })
  return out
}

function useThemeTokens(){
  const [tokens,setTokens]=useState(readTokens)

  useEffect(()=>{
    let frame=null
    const refresh=()=>{
      if (frame) window.cancelAnimationFrame(frame)
      frame=window.requestAnimationFrame(()=>setTokens(readTokens()))
    }

    const observer=new MutationObserver(refresh)
    observer.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme','class','style']})

    let media=null
    if (window.matchMedia){
      media=window.matchMedia('(prefers-color-scheme: dark)')
      if (media.addEventListener) media.addEventListener('change',refresh)
      else if (media.addListener) media.addListener(refresh)
    }

    refresh()

    return ()=>{
      if (frame) window.cancelAnimationFrame(frame)
      observer.disconnect()
      if (media){
        if (media.removeEventListener) media.removeEventListener('change',refresh)
        else if (media.removeListener) media.removeListener(refresh)
      }
    }
  },[])

  return tokens
}

function buildTableStyles(t){
  return {
    table:{ style:{ backgroundColor:t['--panel'], color:t['--ink'] } },
    tableWrapper:{ style:{ display:'block' } },
    head:{ style:{ color:t['--ink-3'] } },
    headRow:{
      style:{
        backgroundColor:t['--panel-raise'],
        color:t['--ink-3'],
        minHeight:'42px',
        borderBottomWidth:'1px',
        borderBottomColor:t['--line'],
        borderBottomStyle:'solid'
      }
    },
    headCells:{
      style:{
        paddingLeft:'12px',
        paddingRight:'12px',
        fontFamily:t['--mono'],
        fontSize:'10px',
        fontWeight:600,
        letterSpacing:'0.08em',
        textTransform:'uppercase',
        color:t['--ink-3']
      }
    },
    rows:{
      style:{
        backgroundColor:t['--panel'],
        color:t['--ink'],
        fontFamily:t['--sans'],
        fontSize:t['--step-0']||'0.875rem',
        minHeight:'50px',
        '&:not(:last-of-type)':{
          borderBottomWidth:'1px',
          borderBottomStyle:'solid',
          borderBottomColor:t['--line-soft']
        }
      },
      highlightOnHoverStyle:{
        backgroundColor:t['--accent-wash'],
        color:t['--ink'],
        borderBottomColor:t['--line-soft'],
        outlineWidth:'0px',
        outlineColor:t['--panel'],
        transitionDuration:'0.15s',
        transitionProperty:'background-color'
      }
    },
    cells:{ style:{ paddingLeft:'12px', paddingRight:'12px' } },
    pagination:{
      style:{
        backgroundColor:t['--panel'],
        color:t['--ink-3'],
        fontSize:'12px',
        minHeight:'50px',
        borderTopWidth:'1px',
        borderTopStyle:'solid',
        borderTopColor:t['--line']
      },
      pageButtonsStyle:{
        color:t['--ink-2'],
        fill:t['--ink-2'],
        backgroundColor:'transparent',
        '&:disabled':{ color:t['--ink-3'], fill:t['--ink-3'] },
        '&:hover:not(:disabled)':{ backgroundColor:t['--panel-raise'] },
        '&:focus':{ outline:'none', backgroundColor:t['--accent-wash'] }
      }
    },
    noData:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } },
    progress:{ style:{ backgroundColor:t['--panel'], color:t['--ink-3'] } }
  }
}

export default function SharedFiles() {
    const [data,setData]=useState([])
    /* true from the first paint: loadData() fires on mount */
    const [isLoading,setIsLoading]=useState(true)
    const [failed,setFailed]=useState(false)
    const [show, setShow] = useState(false)
    const [file,setFile]=useState(null)
    const [fileName,setFileName]=useState('No file selected')
    const [upLoading, setUpLoading]=useState(false)
    const [uploadPercentage, setUploadPercentage]=useState(0)
    const [descr,setDecsr]=useState('')
    const [q,setQ]=useState('')

    /* inline feedback — this is what replaced the alert() calls */
    const [note,setNote]=useState(null)
    const [modalNote,setModalNote]=useState(null)
    /* {uuid, name} while the operator is confirming a delete */
    const [pending,setPending]=useState(null)

    const fileRef=useRef(null)
    const tokens=useThemeTokens()

    /* who may remove a file — unchanged rule: your own upload, or an admin */
    const canDelete=(row)=>(
      row.AdminUserUUID==sessionStorage.getItem('userUUID') || sessionStorage.getItem('isAdmin')==1
    )

    const columns = [
        {
            name: '#',
            selector: (row,index) =>  index+1,
            sortable: false,
            width: '58px',
            center: true
        },
        {
            name: 'User Name',
            selector: row =>  row.UN,
            cell: row => <span className="st-cell-dim">{row.UN}</span>,
            sortable: true,
            minWidth: '150px'
        },
        {
            name: 'File Name',
            selector: row =>  row.FileName,
            cell: row => <span className="st-cell-name">{row.FileName}</span>,
            sortable: true,
            minWidth: '200px',
            wrap: true
        },
        {
            name: '',
            cell: row => (
              <button
                type="button"
                className="st-rowbtn"
                onClick={()=>handleDownload(row.FileUUID,row.FileName)}
                aria-label={'Download '+(row.FileName||'')}
                title="Download"
              >
                <IconDownload/>
              </button>
            ),
            width: '62px',
            center: true,
            ignoreRowClick: true
        },
        {
            name: 'Descripion',
            selector: row =>  row.Descr,
            cell: row => (
              row.Descr
                ? <span className="st-cell-dim">{row.Descr}</span>
                : <span className="st-cell-dim">—</span>
            ),
            sortable: true,
            minWidth: '220px',
            wrap: true
        },
        {
            name: '',
            cell: row => (
              canDelete(row)
                ? <button
                    type="button"
                    className="st-rowbtn st-rowbtn-crit"
                    onClick={()=>setPending({uuid:row.FileUUID,name:row.FileName})}
                    aria-label={'Delete '+(row.FileName||'')}
                    title="Delete"
                  >
                    <IconTrash/>
                  </button>
                : null
            ),
            width: '62px',
            center: true,
            ignoreRowClick: true
        }
    ]

    useEffect(()=>{
        loadData()
    },[])

    /* Escape closes whichever dialog is open. */
    useEffect(()=>{
      if (!show && !pending) return undefined
      const onKey=(ev)=>{
        if (ev.key!=='Escape') return
        if (pending) setPending(null)
        else handleClose()
      }
      document.addEventListener('keydown',onKey)
      return ()=>document.removeEventListener('keydown',onKey)
    },[show,pending])

    const delFile=(uuid,filename)=>{
            axios.get(process.env.REACT_APP_API_SERVER+'admin/sharedfiles/delete/'+uuid,{
                headers: {
                    'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                    }
                })
              .then(res=>{
                loadData()
                setIsLoading(false)
                setPending(null)
                setNote({tone:'good',text:'“'+filename+'” was deleted.'})

              }).catch(err=>{
                  setIsLoading(false)
                  setPending(null)
                  setNote({tone:'crit',text:'“'+filename+'” could not be deleted. Please try again.'})
                  console.log(err)
              })
    }

    const handleDownload = async (uuid,filename) => {
        try {
          const response = await axios.get(process.env.REACT_APP_API_SERVER+'admin/sharedfiles/file/'+uuid, {
            responseType: "blob", // Ensures the response is treated as a Blob
            headers: {
                'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
            }
          });

          // Create a URL for the Blob and trigger the download
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;

          // Set the default file name for download
          link.download = filename;
          link.click();

          // Clean up the URL object
          window.URL.revokeObjectURL(url);
        } catch (error) {
          setNote({tone:'crit',text:'“'+filename+'” could not be downloaded. Please try again.'})
          console.error("Error downloading the file:", error);
        }
      };

    const loadData=()=>{
        setIsLoading(true)
        setFailed(false)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/sharedfiles',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
          setData(res.data.results)
          setIsLoading(false)

        }).catch(err=>{
            setIsLoading(false)
            setFailed(true)
            setNote({tone:'crit',text:'The shared files could not be loaded. Check your connection and try again.'})
            console.log(err)
        })
      }

    const showModal=()=>{
        setModalNote(null)
        setShow(true)
    }

    const handleClose = () => {
      if (upLoading) return
      setShow(false)
      setModalNote(null)
    }

    const resetPicker=()=>{
        if (fileRef.current) fileRef.current.value=null
        setFile(null)
        setFileName('No file selected')
    }

    const save=(e)=>{
        e.preventDefault()
        setModalNote(null)
        const formData=new FormData()
        formData.append('file',file)
        formData.append('userUUID',sessionStorage.getItem('userUUID'))
        formData.append('descr',descr)

        const url=process.env.REACT_APP_API_SERVER+'admin/sharedfiles'
        try{
            setUpLoading(true)
            axios.post(url,formData,{
                headers:{
                    'Content-Type':'multipart/form-data',
                    'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
                },
                onUploadProgress: ProgressEvent=>{
                    setUploadPercentage(parseInt(Math.round(ProgressEvent.loaded*100/ProgressEvent.total)))
                }
            }).then(res=>{
                setUpLoading(false)
                resetPicker()

                setUploadPercentage(0)

                if (res.data.err==0){
                    setDecsr('')
                    loadData()
                    setShow(false)
                    setNote({tone:'good',text:'File uploaded.'})
                }else{
                    setModalNote({tone:'crit',text:'An Error has occured! Please Try Again'})
                }

            }).catch(err=>{
                setUpLoading(false)
                setUploadPercentage(0)
                setModalNote({tone:'crit',text:'The upload failed. Please try again.'})
                console.log(err)
            })

        }catch(err){
            setUpLoading(false)
            setUploadPercentage(0)
            setModalNote({tone:'crit',text:'The upload failed. Please try again.'})
            console.log(err)
        }

    }

    const fileChanged=e=>{
        const picked=e.target.files && e.target.files[0]
        if (!picked){
            resetPicker()
            return
        }
        setFile(picked)
        setFileName(picked.name)
    }

    /* Every colour below comes from theme/tokens.css via getComputedStyle,
       so the table repaints with the rest of the product on a theme change. */
    const tableStyles=useMemo(()=>buildTableStyles(tokens),[tokens])

    const shown=useMemo(()=>{
      const needle=q.trim().toLowerCase()
      if (!needle) return data
      return data.filter(item=>{
        const name=String(item.FileName||'').toLowerCase()
        const who=String(item.UN||'').toLowerCase()
        const what=String(item.Descr||'').toLowerCase()
        return name.indexOf(needle)>-1 || who.indexOf(needle)>-1 || what.indexOf(needle)>-1
      })
    },[data,q])

    const total=data.length

  return (
        <div className="st-page">

          {note
            ? <div className="st-note" data-tone={note.tone} role="alert">
                <span className="st-note-text">{note.text}</span>
                <button type="button" className="st-note-x" aria-label="Dismiss" onClick={()=>setNote(null)}>
                  <IconX/>
                </button>
              </div>
            : null}

          <Panel
            title="Shared Files"
            subtitle={
              isLoading
                ? 'Loading…'
                : total===0
                  ? 'Nothing shared yet'
                  : total+(total===1 ? ' file' : ' files')+' shared'
            }
            actions={
              <>
                {total>0
                  ? <span className="st-search">
                      <IconSearch/>
                      <input
                        className="st-input"
                        type="search"
                        value={q}
                        placeholder="Filter files"
                        aria-label="Filter shared files"
                        onChange={(e)=>setQ(e.target.value)}
                      />
                    </span>
                  : null}
                <Btn variant="accent" onClick={showModal}>+ Upload file</Btn>
              </>
            }
          >
            {isLoading
              ? <div className="st-sk" aria-hidden="true">
                  <span className="skeleton"></span>
                  <span className="skeleton"></span>
                  <span className="skeleton"></span>
                  <span className="skeleton"></span>
                </div>
              : failed
                ? <div className="st-empty">
                    <div className="st-empty-t">The shared files could not be loaded</div>
                    <div className="st-empty-s">
                      The server did not answer. Nothing has been lost — try loading them again.
                    </div>
                    <div className="st-empty-a">
                      <Btn variant="accent" onClick={loadData}>Try again</Btn>
                    </div>
                  </div>
                : total===0
                  ? <div className="st-empty">
                      <div className="st-empty-t">No shared files yet</div>
                      <div className="st-empty-s">
                        Upload a file here to hand it to the rest of the back-office team.
                        Everyone can download it; you and the admins can remove it.
                      </div>
                      <div className="st-empty-a">
                        <Btn variant="accent" onClick={showModal}>+ Upload file</Btn>
                      </div>
                    </div>
                  : shown.length===0
                    ? <div className="st-empty">
                        <div className="st-empty-t">No file matches “{q.trim()}”</div>
                        <div className="st-empty-s">
                          {total} {total===1 ? 'file is' : 'files are'} shared. Clear the filter to see
                          {total===1 ? ' it' : ' them all'}.
                        </div>
                        <div className="st-empty-a">
                          <Btn onClick={()=>setQ('')}>Clear filter</Btn>
                        </div>
                      </div>
                    : <div className="st-table">
                        <DataTable
                          columns={columns}
                          data={shown}
                          customStyles={tableStyles}
                          highlightOnHover
                          persistTableHead
                          pagination
                          paginationPerPage={25}
                          paginationRowsPerPageOptions={[25,50,100]}
                          noDataComponent={<div className="st-empty-s">No files to show.</div>}
                        />
                      </div>}
          </Panel>

          {show
            ? <div className="st-modal" role="dialog" aria-modal="true" aria-labelledby="st-sf-title">
                <div className="st-modal-panel">

                  <div className="st-modal-head">
                    <div className="st-modal-headline">
                      <div className="st-modal-title" id="st-sf-title">Upload a shared file</div>
                      <div className="st-modal-sub">Visible to every back-office user.</div>
                    </div>
                    <button
                      type="button"
                      className="st-modal-x"
                      aria-label="Close"
                      disabled={upLoading}
                      onClick={handleClose}
                    >
                      <IconX/>
                    </button>
                  </div>

                  <div className="st-modal-body">

                    {modalNote
                      ? <div className="st-note" data-tone={modalNote.tone} role="alert">
                          <span className="st-note-text">{modalNote.text}</span>
                        </div>
                      : null}

                    <form className="st-form" onSubmit={save}>

                      <div className="st-field">
                        <span className="mono st-label">Select File</span>
                        <div className="st-file-row">
                          <input
                            ref={fileRef}
                            className="st-file"
                            onChange={fileChanged}
                            type="file"
                            required
                            id="customFile"
                          />
                          <label className="st-file-label" htmlFor="customFile">
                            <IconPaperclip/>
                            Choose file
                          </label>
                          <span className="st-file-name" data-set={file ? '1' : '0'}>{fileName}</span>
                        </div>
                      </div>

                      <div className="st-field">
                        <label className="mono st-label" htmlFor="st-sf-descr">Short Description</label>
                        <input
                          id="st-sf-descr"
                          className="st-input"
                          type="text"
                          maxLength={200}
                          required
                          value={descr}
                          onChange={(e)=>setDecsr(e.target.value)}
                        />
                        <div className="st-hint">Up to 200 characters — what the file is for.</div>
                      </div>

                      {upLoading
                        ? <div className="st-field">
                            <div className="st-up-progress">
                              <span className="st-up-fill" style={{width:uploadPercentage+'%'}}></span>
                            </div>
                            <div className="st-up-pct">
                              <b>{uploadPercentage}%</b>
                              <span>Uploading — please wait.</span>
                            </div>
                          </div>
                        : null}

                      <div className="st-foot">
                        <Btn onClick={handleClose} disabled={upLoading}>Cancel</Btn>
                        <Btn variant="accent" type="submit" disabled={upLoading}>Upload</Btn>
                      </div>

                    </form>

                  </div>
                </div>
              </div>
            : null}

          {pending
            ? <div className="st-modal" role="dialog" aria-modal="true" aria-labelledby="st-sf-del-title">
                <div className="st-modal-panel">

                  <div className="st-modal-head">
                    <div className="st-modal-headline">
                      <div className="st-modal-title" id="st-sf-del-title">Delete the file {pending.name}?</div>
                      <div className="st-modal-sub">This removes it for everyone and cannot be undone.</div>
                    </div>
                    <button type="button" className="st-modal-x" aria-label="Close" onClick={()=>setPending(null)}>
                      <IconX/>
                    </button>
                  </div>

                  <div className="st-modal-body">
                    <div className="st-foot">
                      <Btn onClick={()=>setPending(null)}>Cancel</Btn>
                      <Btn className="st-crit" onClick={()=>delFile(pending.uuid,pending.name)}>Delete file</Btn>
                    </div>
                  </div>
                </div>
              </div>
            : null}

        </div>
  )
}
